// Split-by group assembly for the info-view compare-items block, ported from the coffee view
// (applySplitBy). Groups come from four sources - demographic traits, report virtual variables,
// survey variables (plain / piping / custom) - each narrowed to the values a crosstab can render.

import {
  getCustomVariables,
  getGeoTraits,
  getFeature,
  getReportVirtual,
  getSurveyOptions,
  getSurveyRefsList,
  getSurveyVariables,
  getTraits
} from '@aytm/app/stats/lib/stat-page'
import { heldBackVariables } from '@aytm/app/stats/lib/page/held-back-variables'

export const DEFAULT_SPLIT_BY = { ref: null, group: 'items' }

const isSplittable = values => 1 < values?.length && 21 > values?.length

const traitId = id => ('age' === id && 'age_range') || id

const calcTraitGroups = (traits = [], geoTraits = [], crosstabTraits) =>
  traits
    .filter(
      trait =>
        (crosstabTraits?.includes(trait.id) || (null == crosstabTraits && !geoTraits.includes(trait.id))) &&
        !['o', 'a'].includes(trait.type) &&
        true !== trait.missing &&
        isSplittable(trait.values)
    )
    .map(trait => ({ id: traitId(trait.id), title: trait.title, values: trait.values }))

const calcVirtualGroups = (virtual = {}) =>
  Object.keys(virtual)
    .filter(
      key => 'dlv' === virtual[key].type && 'rb' === virtual[key].variant && isSplittable(virtual[key].answerVariants)
    )
    .map(key => ({
      id: virtual[key].id,
      title: virtual[key].id.toUpperCase(),
      values: virtual[key].answerVariants.map(av => ({ id: `a${av.position}`, name: `A${av.position}: ${av.text}` }))
    }))

const calcVariableGroups = (variables = {}, refsList = {}, rejected = []) =>
  Object.keys(variables)
    .filter(
      key =>
        !rejected.includes(key) &&
        0 < variables[key].values.length &&
        !['o', 'a', 'x'].includes(variables[key].type) &&
        '___' !== key.slice(0, 3) &&
        Object.hasOwn(refsList, key.toUpperCase()) &&
        isSplittable(variables[key].values)
    )
    .sort()
    .map(key => ({ id: key, title: key, values: variables[key].values.map(value => ({ id: value, name: value })) }))

// The live page state the groups are built from, read in one place so calcSplitGroups stays pure.
export const readSplitBySources = () => ({
  traits: getTraits(),
  geoTraits: getGeoTraits(),
  crosstabTraits: getSurveyOptions().crosstabTraits,
  virtual: getReportVirtual(),
  variables: getSurveyVariables('variables'),
  pipingVariables: getSurveyVariables('pipingVariables'),
  customVariables: getCustomVariables(),
  refsList: getSurveyRefsList(),
  hideStatusVariable: Boolean(getFeature('fielding_health_status')?.disabled)
})

export const calcSplitGroups = ({
  traits,
  geoTraits,
  crosstabTraits,
  virtual,
  variables,
  pipingVariables,
  customVariables,
  refsList,
  hideStatusVariable = false,
  isAdmin
} = {}) => {
  const rejected = heldBackVariables({
    isAdmin,
    features: { fielding_health_status: { disabled: hideStatusVariable } },
    refsList,
    identity: true
  })

  return [
    ...calcTraitGroups(traits, geoTraits, crosstabTraits),
    ...calcVirtualGroups(virtual),
    ...calcVariableGroups(variables, refsList, rejected),
    ...calcVariableGroups(pipingVariables, refsList),
    ...calcVariableGroups(customVariables, refsList)
  ]
}
